import React, { useState, useEffect, useMemo } from 'react';
import axios from 'axios';
import GetCookie from '../../utils/GetCookie';
import { StatusBadgeSm, StatusBadgeXSm } from '../../utils/StatusBadge';
import DocumentPreview from '../../utils/DocumentPreview';
import SourceTooltip from './SourceTooltip';
import ProcessingPopup from '../../utils/popup/ProcessingPopup';
import ErrorPopup from '../../utils/popup/ErrorPopup';
import SuccessPopup from '../../utils/popup/SuccessPopup';
import PaginationControls from '../../utils/PaginationControls';
import DeleteConfirmationModal from '../../utils/DeleteConfirmationModal';
import ConfirmationModal from '../../utils/ConfirmationModal';
import TrimContent from '../../utils/TrimContent';
import TextractorThreadPanel from './TextractorThreadPanel';
import authNavigate from '../../globalComponents/AuthNav';
import { useNavigate } from 'react-router-dom';

// Read mode: cell text longer than this is trimmed with a "View more" toggle
const CELL_TRIM_LENGTH = 50;

const ModelDataViewer = ({ modelId, modelName, fabricId, modelDataCache, setModelDataCache, gridThreadIdFromSLR }) => {
  const [data, setData] = useState([]);
  const [columns, setColumns] = useState([]);
  const [tableName, setTableName] = useState('');
  const [tableStatus, setTableStatus] = useState('');
  const [page, setPage] = useState(1);
  const [size, setSize] = useState(25);
  const [search, setSearch] = useState('');
  const [searchInput, setSearchInput] = useState('');
  const [total, setTotal] = useState(0);
  const [isLoading, setIsLoading] = useState(false);
  const [isInitialLoading, setIsInitialLoading] = useState(!modelDataCache[modelId]);
  const [error, setError] = useState(null);
  const [isSLRFabric, setIsSLRFabric] = useState(false);
  const [documentStatuses, setDocumentStatuses] = useState([]);
  const [statusHoverInfo, setStatusHoverInfo] = useState(null);

  // Document preview states
  const [previewOpen, setPreviewOpen] = useState(false);
  const [previewDocId, setPreviewDocId] = useState(null);
  const [previewDocumentName, setPreviewDocumentName] = useState(null);
  const [previewHighlightText, setPreviewHighlightText] = useState(null);
  const [previewChunkContent, setPreviewChunkContent] = useState(null);

  // Edit mode states
  const [isEditMode, setIsEditMode] = useState(false);
  const [editedRows, setEditedRows] = useState({});
  const [hasPendingChanges, setHasPendingChanges] = useState(false);
  const [isSelectedDel, setIsSelectedDel] = useState([]);

  // Read-mode cells the user has expanded with "View more" ("view:<row>:<col>")
  const [expandedCells, setExpandedCells] = useState({});
  
  // Modal states
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [showRunConfirm, setShowRunConfirm] = useState(false);
  const [pendingExitEditMode, setPendingExitEditMode] = useState(false);
  const [pendingRefreshDiscard, setPendingRefreshDiscard] = useState(false);
  const [pendingRunAction, setPendingRunAction] = useState(null);
  const [showRestartConfirm, setShowRestartConfirm] = useState(false);
  const [showThreadPanel, setShowThreadPanel] = useState(false);
  
  // Popup states
  const [isProcessingPopupVisible, setIsProcessingPopupVisible] = useState(false);
  const [processingMessage, setProcessingMessage] = useState('');
  const [isErrorPopupVisible, setIsErrorPopupVisible] = useState(false);
  const [popupErrorMessage, setPopupErrorMessage] = useState('');
  const [isSuccessPopupVisible, setIsSuccessPopupVisible] = useState(false);
  const [popupSuccessMessage, setPopupSuccessMessage] = useState('');

  const navigate = useNavigate();

  const API_BASE_URL = '/pceapi/textractor';
  const headers = {
    Authorization: GetCookie('access_token')
  };
  
  // Load cached data if available
  useEffect(() => {
    if (modelDataCache[modelId]) {
      const cachedData = modelDataCache[modelId];
      setData(cachedData.data);
      setColumns(cachedData.columns);
      setTableName(cachedData.table_name);
      setTableStatus(cachedData.table_status);
      setPage(cachedData.page);
      setSize(cachedData.size);
      setSearch(cachedData.search);
      setSearchInput(cachedData.search);
      setTotal(cachedData.total);
      setIsSLRFabric(cachedData.is_slr_fabric || false);
      setDocumentStatuses(cachedData.document_statuses || []);
      setIsInitialLoading(false);
    } else {
      // Reset state when viewing different model
      setData([]);
      setColumns([]);
      setTableName('');
      setTableStatus('');
      setPage(1);
      setSize(20);
      setSearch('');
      setSearchInput('');
      setTotal(0);
      setError(null);
      setIsSLRFabric(false);
      setDocumentStatuses([]);
      setIsInitialLoading(true);
      fetchModelData();
    }
    
    // Reset edit states when changing models
    setIsEditMode(false);
    setEditedRows({});
    setHasPendingChanges(false);
  }, [modelId]);

  // Collapse all expanded cells when the sheet or page changes
  useEffect(() => {
    setExpandedCells({});
  }, [modelId, page, size, search]);

  // Fetch model data when pagination/search changes
  useEffect(() => {
    if (modelDataCache[modelId]?.page !== page || 
        modelDataCache[modelId]?.size !== size || 
        modelDataCache[modelId]?.search !== search) {
      fetchModelData();
    }
  }, [page, size, search]);

  // Reset edited rows when toggling edit mode off
  useEffect(() => {
    if (!isEditMode) {
      setEditedRows({});
      setHasPendingChanges(false);
    }
  }, [isEditMode]);

  const statusByDocId = useMemo(() => {
    const map = {};
    (documentStatuses || []).forEach(ds => {
      if (ds && ds.doc_id != null) {
        map[ds.doc_id] = ds;
      }
    });
    return map;
  }, [documentStatuses]);

  // Synthetic rows for FAILED docs not present in the data array.
  const syntheticFailedRows = useMemo(() => {
    const baseData = Array.isArray(data) ? data : [];
    const existingDocIds = new Set(baseData.map(r => r.doc_id));
    return (documentStatuses || [])
      .filter(ds => ds && ds.status === 'FAILED' && !existingDocIds.has(ds.doc_id))
      .map(ds => ({
        id: null,
        doc_id: ds.doc_id,
        document_name: ds.document_name,
        __synthetic: true
      }));
  }, [data, documentStatuses]);

  // Append synthetic rows only on page 1 so they don't duplicate across pages.
  const mergedData = useMemo(() => {
    const baseData = Array.isArray(data) ? data : [];
    if (page !== 1) return baseData;
    return [...baseData, ...syntheticFailedRows];
  }, [data, syntheticFailedRows, page]);

  const handleStatusBadgeEnter = (e, docStatus) => {
    if (!docStatus || !docStatus.notes) return;
    const rect = e.currentTarget.getBoundingClientRect();
    setStatusHoverInfo({
      bottom: window.innerHeight - rect.top + 6,
      left: rect.left,
      notes: docStatus.notes
    });
  };

  const handleStatusBadgeLeave = () => setStatusHoverInfo(null);

  const fetchModelData = async () => {
    setIsLoading(true);
    setError(null);

    try {
      const response = await axios.post(
        `${API_BASE_URL}/get-model-data`,
        {
          table_id: modelId,
          page,
          size,
          search: search || undefined
        },
        { headers }
      );

      const result = response.data;

      if (result.status === 'NotAuthorized') {
        authNavigate('/login', 'iam', navigate);
        return;
      }

      if (result.status === 'failed') {
        setError(result.message || 'Failed to load model data');
        return;
      }

      setData(result.data);
      setColumns(result.columns);
      setTableName(result.table_name);
      setTableStatus(result.table_status);
      setTotal(result.total);
      setIsSLRFabric(result.is_slr_fabric || false);
      setDocumentStatuses(result.document_statuses || []);

      // Cache the data
      const cacheData = {
        data: result.data,
        columns: result.columns,
        table_name: result.table_name,
        table_status: result.table_status,
        page,
        size,
        search,
        total: result.total,
        is_slr_fabric: result.is_slr_fabric || false,
        document_statuses: result.document_statuses || []
      };

      setModelDataCache(prev => ({
        ...prev,
        [modelId]: cacheData
      }));

    } catch (error) {
      console.error('Error fetching sheet data:', error);
      setError(error.message || 'Failed to load sheet data');
    } finally {
      setIsLoading(false);
      setIsInitialLoading(false);
    }
  };

  // Handle cell value change
  const handleCellChange = (rowIndex, columnName, value) => {
    const currentRowData = data[rowIndex];
    
    if (!currentRowData || !currentRowData.doc_id) {
      console.error("Row missing required doc_id", currentRowData);
      setPopupErrorMessage("Cannot edit row: missing document ID");
      setIsErrorPopupVisible(true);
      return;
    }
    
    const docId = currentRowData.doc_id;
      setEditedRows(prev => {
      const existing = prev[docId];
      return {
        ...prev,
        [docId]: existing
          ? { ...existing, [columnName]: value, id: existing.id ?? currentRowData.id }
          : { doc_id: docId, id: currentRowData.id, [columnName]: value },
      };
    });
    setHasPendingChanges(true);

    setData(prevData =>
      prevData.map((row, idx) =>
        idx === rowIndex ? { ...row, [columnName]: value } : row
      )
    );
  };

  // Save edited data
  const saveEditedData = async () => {
    if (!Object.keys(editedRows).length) {
      return;
    }
    
    setIsProcessingPopupVisible(true);
    setProcessingMessage(`Saving changes to ${tableName}`);
    
    try {
      const rowsToUpdate = Object.values(editedRows).map(row => {
        const rowData = {};
        
        rowData.doc_id = row.doc_id;
        if (row.id) {
          rowData.id = row.id;
        }
        
        columns.forEach(col => {
          if (row[col.name] !== undefined) {
            rowData[col.name] = row[col.name];
          }
        });
        
        return rowData;
      });
      
      const validRows = rowsToUpdate.filter(row => row.doc_id);
      
      if (validRows.length !== rowsToUpdate.length) {
        const missingRows = rowsToUpdate.length - validRows.length;
        setPopupErrorMessage(`${missingRows} row(s) are missing required document ID and will be skipped`);
        setIsErrorPopupVisible(true);
        setIsProcessingPopupVisible(false);
        return;
      }
      
      const response = await axios.post(
        `${API_BASE_URL}/update-model-data`,
        {
          table_id: modelId,
          fabric_id: fabricId,
          rows: validRows
        },
        { headers }
      );
      
      const result = response.data;
      
      if (result.status === 'failed') {
        setPopupErrorMessage(result.message || 'Failed to save changes');
        setIsErrorPopupVisible(true);
        return;
      }
      
      setEditedRows({});
      setHasPendingChanges(false);
      
      setPopupSuccessMessage(result.message || 'Changes saved successfully');
      setIsSuccessPopupVisible(true);
      
      setModelDataCache(prev => ({
        ...prev,
        [modelId]: {
          ...prev[modelId],
          data: data
        }
      }));
      
    } catch (error) {
      console.error('Error saving data:', error);
      setPopupErrorMessage(error.message || 'Failed to save changes');
      setIsErrorPopupVisible(true);
    } finally {
      setIsProcessingPopupVisible(false);
    }
  };

  // Toggle edit mode
  const toggleEditMode = (enableEdit) => {
    if (isEditMode && hasPendingChanges && !enableEdit) {
      setPendingExitEditMode(true);
      return;
    }
    setIsEditMode(enableEdit);
  };

  const confirmExitAndSave = () => {
    setPendingExitEditMode(false);
    saveEditedData();
    setIsEditMode(false);
  };

  const confirmExitWithoutSaving = () => {
    setPendingExitEditMode(false);
    setIsEditMode(false);
  };

  // Run model
  const handleRunModel = async (retrigger = false) => {
    setShowRunConfirm(false);
    setShowRestartConfirm(false);
    
    if (!fabricId) {
      setPopupErrorMessage('Missing fabric ID information. Please refresh the page and try again.');
      setIsErrorPopupVisible(true);
      return;
    }
    
    setIsProcessingPopupVisible(true);
    setProcessingMessage(retrigger ? `Restarting${tableName}` : `Running ${tableName}`);
    
    try {
      const response = await axios.post(
        `${API_BASE_URL}/run-model`,
        {
          table_id: modelId,
          fabric_id: fabricId,
          retrigger
        },
        { headers }
      );
      
      const result = response.data;
      
      if (result.status === 'NotAuthorized') {
        authNavigate('/login', 'iam', navigate);
        return;
      }
      
      if (result.status === 'failed') {
        setPopupErrorMessage(result.message || 'Failed to run');
        setIsErrorPopupVisible(true);
        return;
      }
      
      setPopupSuccessMessage(result.message || 'Sheet operation successful');
      setIsSuccessPopupVisible(true);
      
      setTableStatus('RUNNING');
      
      setModelDataCache(prev => ({
        ...prev,
        [modelId]: {
          ...prev[modelId],
          table_status: 'RUNNING'
        }
      }));
      
    } catch (error) {
      console.error('Error running:', error);
      setPopupErrorMessage(error.message || 'Failed to run');
      setIsErrorPopupVisible(true);
    } finally {
      setIsProcessingPopupVisible(false);
    }
  };

  // Download data
  const handleDownload = async () => {
    try {
      const response = await axios.post(
        `${API_BASE_URL}/download-model`,
        { table_id: modelId },
        { 
          headers,
          responseType: 'blob'
        }
      );

      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `${tableName}.csv`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Error downloading model data:', error);
      setError('Failed to download model data');
    }
  };

  const handleDirectMigrate = async () => {
    if (!gridThreadIdFromSLR) return;

    setIsProcessingPopupVisible(true);
    setProcessingMessage('Migrating CSV data to grid Data Collection...');

    try {
      const threadDetailsResponse = await axios.get(
        `/pceapi/threads/unstructured/details/${gridThreadIdFromSLR}`,
        { headers }
      );

      const threadDetailsResult = threadDetailsResponse.data;

      if (threadDetailsResult.status === 'NotAuthorized') {
        authNavigate('/login', 'iam', navigate);
        return;
      }

      if (threadDetailsResult.status !== 'success' || !threadDetailsResult.data) {
        setPopupErrorMessage(threadDetailsResult.message || 'Failed to fetch grid Data Collection details');
        setIsErrorPopupVisible(true);
        return;
      }

      const threadData = threadDetailsResult.data.thread;

      if (!threadData) {
        setPopupErrorMessage('Failed to fetch grid Data Collection details');
        setIsErrorPopupVisible(true);
        return;
      }

      const downloadResponse = await axios.post(
        `${API_BASE_URL}/download-model`,
        { table_id: modelId },
        { headers, responseType: 'blob' }
      );

      const filename = `${tableName || modelName || 'extraction_data'}.csv`;
      const file = new File([downloadResponse.data], filename, { type: 'text/csv' });

      const formData = new FormData();
      formData.append('thread_id', gridThreadIdFromSLR);
      formData.append('name', threadData.name || 'Grid Data Collection');
      formData.append('thread_access_level', threadData.thread_access_level || 'PRIVATE');
      formData.append('tags', JSON.stringify(threadData.tags || []));
      const userAccessIds = (threadData.user_access || []).map(u => u.user_id || u);
      formData.append('user_access', JSON.stringify(userAccessIds));
      formData.append('csvFiles', file);

      const uploadResponse = await axios.post(
        '/pceapi/threads/unstructured/update',
        formData,
        {
          headers: {
            Authorization: GetCookie('access_token'),
            'Content-Type': 'multipart/form-data'
          }
        }
      );

      if (uploadResponse.data.status === 'success') {
        setPopupSuccessMessage('CSV data migrated to grid Data Collection successfully');
        setIsSuccessPopupVisible(true);
      } else {
        setPopupErrorMessage(uploadResponse.data.message || 'Failed to migrate CSV to grid Data Collection');
        setIsErrorPopupVisible(true);
      }
    } catch (error) {
      console.error('Error in direct migrate to grid Data Collection:', error);
      setPopupErrorMessage(error.response?.data?.message || error.message || 'Failed to migrate CSV to grid Data Collection');
      setIsErrorPopupVisible(true);
    } finally {
      setIsProcessingPopupVisible(false);
    }
  };

  // Handle search input change
  const handleSearchInputChange = (e) => {
    setSearchInput(e.target.value);
  };

  // Handle search button click
  const handleSearch = () => {
    setSearch(searchInput);
    setPage(1);
  };

  // Safe function
  const getAllIds = () => {
    if (!data || !Array.isArray(data)) return [];
    return data.map(item => item.id);
  };

  const handleSelectAll = (checked) => {
    if (checked) {
      setIsSelectedDel(getAllIds());
    } else {
      setIsSelectedDel([]);
    }
  };

  const handleIndividualDelSelect = (id, checked) => {
    if (checked) {
      setIsSelectedDel(prev => [...prev, id]);
    } else {
      setIsSelectedDel(prev => prev.filter(selectedId => selectedId !== id));
    }
  };

  const callDeleteApi = async () => {
    if (isSelectedDel.length === 0) {
      setPopupErrorMessage('No IDs selected for deletion');
      setIsErrorPopupVisible(true);
      return;
    }
    
    setIsProcessingPopupVisible(true);
    setProcessingMessage(`Deleting selected data from model "${tableName}"`);
    
    try {
      const response = await axios.post(
        `${API_BASE_URL}/delete-model-data`,
        {
          table_id: modelId,
          fabric_id: fabricId,
          row_ids: isSelectedDel
        },
        { headers }
      );

      const result = response.data;

      if (result.status === 'NotAuthorized') {
        authNavigate('/login', 'iam', navigate);
        return;
      }

      if (result.status === 'failed') {
        setIsProcessingPopupVisible(false);
        setPopupErrorMessage(result.message || 'Failed to delete data');
        setIsErrorPopupVisible(true);
        return;
      }
      
      setIsProcessingPopupVisible(false);
      setPopupSuccessMessage(result.message || 'Data deleted successfully');
      setIsSuccessPopupVisible(true);

      const deletedDocIds = new Set(
        mergedData.filter(r => isSelectedDel.includes(r.id)).map(r => r.doc_id)
      );
      if (deletedDocIds.size > 0) {
        setEditedRows(prev => {
          const next = { ...prev };
          let changed = false;
          for (const docId of Object.keys(next)) {
            if (deletedDocIds.has(Number(docId)) || deletedDocIds.has(docId)) {
              delete next[docId];
              changed = true;
            }
          }
          return changed ? next : prev;
        });
      }

      fetchModelData();
    } catch (error) {
      console.error('Error deleting data:', error);
      setIsProcessingPopupVisible(false);
      setPopupErrorMessage(error.message || 'Failed to delete data');
      setIsErrorPopupVisible(true);
    } finally {
      setIsSelectedDel([]);
    }
  };

  const handleDeleteClickedByButton = (id) => {
    if (!id) {
      setPopupErrorMessage('No ID provided for deletion');
      setIsErrorPopupVisible(true);
      return;
    }
    setIsSelectedDel(id ? [id] : []);
    setShowDeleteConfirm(true);
  };

  const handleConfirmDelete = () => {
    setShowDeleteConfirm(false);
    if (isSelectedDel.length > 0) {
      callDeleteApi();
    } else {
      setPopupErrorMessage('No IDs selected for deletion');
      setIsErrorPopupVisible(true);
    }
  };

  // Handle Enter key press in search input
  const handleKeyPress = (e) => {
    if (e.key === 'Enter') {
      handleSearch();
    }
  };

  // Refresh data
  const handleRefresh = () => {
    setIsSelectedDel([]);
    if (isEditMode && hasPendingChanges) {
      setPendingRefreshDiscard(true);
      return;
    }
    fetchModelData();
  };

  const confirmDiscardAndRefresh = () => {
    setPendingRefreshDiscard(false);
    fetchModelData();
    setIsEditMode(false);
  };

  const handlePreviewDocument = (doc_id, documentName) => {
    setPreviewDocId(doc_id);
    setPreviewDocumentName(documentName);
    setPreviewOpen(true);
  };

  const closePreview = () => {
    setPreviewOpen(false);
    setPreviewDocId(null);
    setPreviewDocumentName(null);
    setPreviewHighlightText(null);
    setPreviewChunkContent(null);
  };

  // Phase 2: opens PDF with chunk_content highlighted (range), verbatim_phrase as fallback
  const handleSourceViewClick = (source, verbatimPhrase) => {
    const matchingRow = data.find(row => row.doc_id === source.doc_id);
    const documentName = matchingRow?.document_name || source.doc_title || 'Document';
    const cleanedPhrase = verbatimPhrase
      ? verbatimPhrase.replace(/[\r\n]+/g, ' ').replace(/\s+/g, ' ').trim()
      : null;

    setPreviewDocId(source.doc_id);
    setPreviewDocumentName(documentName);
    setPreviewHighlightText(cleanedPhrase);
    setPreviewChunkContent(source.chunk_content || null);
    setPreviewOpen(true);
  };

  // Close popups
  const closeErrorPopup = () => {
    setIsErrorPopupVisible(false);
  };

  const closeSuccessPopup = () => {
    setIsSuccessPopupVisible(false);
    fetchModelData();
  };

  // Generate body content for delete modal
  const getDeleteModalBodyContent = () => {
    return (
      <div>
        <div className="mb-2">
          <strong>Sheet:</strong> {tableName}
        </div>
        <div className="mb-3">
          <strong>Selected Items:</strong> {isSelectedDel.length} row(s)
        </div>
        <p className="text-danger mb-0">This will permanently delete the selected data from the sheet. This action cannot be undone.</p>
      </div>
    );
  };

  // Generate body content for run modal
  const getRunModalBodyContent = () => {
    return (
      <div>
        {tableStatus === 'COMPLETED' && (
          <p className="text-danger">This sheet has already been processed. To reprocess, use the Restart option.</p>
        )}
      </div>
    );
  };

  // Generate body content for restart modal
  const getRestartModalBodyContent = () => {
    return (
      <div>
        <p className="text-danger">This will clear all existing data in the model and start the extraction process from scratch.</p>
      </div>
    );
  };

  // Render cell with edit support
  const getCellKey = (row, rowIndex, columnName) => `${row.id ?? `r${rowIndex}`}:${columnName}`;

  const toggleCellExpanded = (key) => {
    setExpandedCells(prev => ({ ...prev, [key]: !prev[key] }));
  };

  const renderCell = (row, rowIndex, column) => {
    if (isEditMode) {
      const value = row[column.name] !== null && row[column.name] !== undefined 
        ? row[column.name].toString() 
        : '';
      
      if (column.datatype.toUpperCase().includes('INT')) {
        return (
          <input
            type="number"
            className="formField3  ev-btn-reg"
            value={value}
            onChange={(e) => handleCellChange(rowIndex, column.name, e.target.value ? parseInt(e.target.value) : null)}
          />
        );
      } else if (column.datatype.toUpperCase().includes('FLOAT') || column.datatype.toUpperCase().includes('DECIMAL') || column.datatype.toUpperCase().includes('NUMERIC')) {
        return (
          <input
            type="number"
            step="0.01"
            className="formField3 ev-btn-reg"
            value={value}
            onChange={(e) => handleCellChange(rowIndex, column.name, e.target.value ? parseFloat(e.target.value) : null)}
          />
        );
      } else if (column.datatype.toUpperCase().includes('BOOL')) {
        return (
          <select
            className="formField3 ev-btn-reg"
            value={value}
            onChange={(e) => handleCellChange(rowIndex, column.name, e.target.value === 'true')}
          >
            <option value="true">True</option>
            <option value="false">False</option>
          </select>
        );
      } else if (column.datatype.toUpperCase().includes('DATE')) {
        return (
          <input
            type="date"
            className="formField3 ev-btn-reg"
            value={value.substring(0, 10)}
            onChange={(e) => handleCellChange(rowIndex, column.name, e.target.value)}
          />
        );
      } else {
        // Textarea instead of a single-line input so long content is editable;
        // the user stretches/shrinks it by dragging its bottom-right corner.
        return (
          <textarea
            rows={2}
            className="formField3 ev-btn-reg min-w-200"
            value={value}
            onChange={(e) => handleCellChange(rowIndex, column.name, e.target.value)}
          />
        );
      }
    } else {
      const hasValue = row[column.name] !== null && row[column.name] !== undefined;
      const fullText = hasValue ? row[column.name].toString() : null;

      // Long content is trimmed; "View more" shows the full text, "View less" trims it again
      const viewKey = `view:${getCellKey(row, rowIndex, column.name)}`;
      const isLong = !!fullText && fullText.length > CELL_TRIM_LENGTH;
      const isExpanded = !!expandedCells[viewKey];

      const value = !hasValue
        ? <strong>-</strong>
        : (isLong && !isExpanded ? `${fullText.slice(0, CELL_TRIM_LENGTH).trimEnd()}...` : fullText);

      const toggle = isLong ? (
        <button
          type="button"
          className="ev-btn-lightblue-fill-round ev-btn-xsmall ev-bold-med text-nowrap d-inline-block mt-1"
          onClick={() => toggleCellExpanded(viewKey)}
        >
          {isExpanded ? 'View less' : 'View more'}
        </button>
      ) : null;

      const sourceMapEntry = row.source_map?.[column.name];

      if (sourceMapEntry?.sources?.length > 0) {
        return (
          <>
            <SourceTooltip
              value={value}
              sources={sourceMapEntry.sources}
              verbatimPhrase={sourceMapEntry.verbatim_phrase}
              onViewClick={handleSourceViewClick}
            />
            {toggle}
          </>
        );
      }

      return (
        <>
          {value}
          {toggle}
        </>
      );
    }
  };

  const getTotalColSpan = () => {
    let count = columns.length + 1;
    if (isSLRFabric) count += 2;
    if (isEditMode) count += 2;
    return count;
  };

  // Show processing popup during initial loading
  if (isInitialLoading) {
    return <ProcessingPopup message={`Fetching ${modelName || tableName || 'model'} data`} />;
  }
  
  const isAllSelected = data && isSelectedDel.length === data.length && data.length > 0;
  const isSomeSelected = data && isSelectedDel.length > 0 && isSelectedDel.length < data.length;

  return (
    <div className="ev-card-4 w-100 ev-padding-med vh-75">
      <div className="d-flex justify-content-between align-items-center margin-bottom-1">
        <h5 className="ev-med-p-blue">{tableName}
          <span className="ms-2"> <StatusBadgeSm status={tableStatus} /></span>
        </h5>

        <div className="d-flex align-items-center gap-2">
          {isEditMode && (
              <button
                className={`ev-btn-red-fill-round ev-bold-reg avatar-tooltip`}
                onClick={() => setShowDeleteConfirm(true)}
                data-tooltip={isSelectedDel.length > 0 ? 'Delete Selected' : 'Select rows to delete'}
                disabled={isLoading || (isSelectedDel.length === 0)}
              >
                Delete Selected
              </button>
          )}

          <div className="btn-group custom-btn-group-outer">
            <button 
              className={`btn custom-btn-group-inner ${!isEditMode ? 'ev-btn-blue-fill-round' : 'ev-btn-lightblue-fill-round'}`}
              onClick={() => {toggleEditMode(false); setIsSelectedDel([]);}}
            >
              <i className="bi bi-eye"></i> Read
            </button>
            <button 
              className={`btn custom-btn-group-inner ${isEditMode ? 'ev-btn-blue-fill-round' : 'ev-btn-lightblue-fill-round'}`}
              onClick={() => {toggleEditMode(true)}}
            >
              <i className="bi bi-pencil"></i> Edit Mode
            </button>
          </div>
        </div>
      </div>
      
      <div className="d-flex justify-content-between margin-bottom-1">
        <div className="position-relative w-30">
          <input
            type="text"
            className="form-control search-input ms-0"
            placeholder="Search data"
            value={searchInput}
            onChange={handleSearchInputChange}
            onKeyPress={handleKeyPress}
          />
          <button 
            className="ev-btn-lightblue-fill-round search-bar-button" 
            onClick={handleSearch}
            disabled={isLoading}
          >
            <i className="bi bi-search"></i>
          </button>
        </div>
        
        <div className="d-flex flex-wrap align-items-center gap-1">
          {isEditMode && hasPendingChanges && (
            <button 
              className="ev-btn-yellow-fill-round ev-bold-reg" 
              onClick={saveEditedData}
            >
              Save Edits
            </button>
          )}

          <div className="btn-group custom-btn-group-outer ms-1">
            <button 
              className={`btn custom-btn-group-inner ev-btn-gradient-fill-round avatar-tooltip`}
              onClick={() => (isEditMode && hasPendingChanges) ? setPendingRunAction('run') : setShowRunConfirm(true)}
              data-tooltip={tableStatus === 'RUNNING' ? 'Running' : 'Soft Run'}
              disabled={tableStatus === 'RUNNING' || isLoading}
            >
              <i className="bi bi-play-fill"></i> Run 
            </button>
            <button 
              className={`btn custom-btn-group-inner ev-btn-white-fill-round avatar-tooltip`}
              onClick={() => (isEditMode && hasPendingChanges) ? setPendingRunAction('restart') : setShowRestartConfirm(true)}
              data-tooltip={tableStatus === 'RUNNING' ? 'Running' : 'Hard Run (Reset model and restart again)'}
              disabled={tableStatus === 'RUNNING' || isLoading}
            >
              <i className="bi bi-play-fill"></i> Restart
            </button>
          </div>
          
          <button 
            className="ev-btn-blue-outline-round ev-bold-reg ms-1" 
            onClick={handleDownload}
            disabled={isLoading || tableStatus !== 'COMPLETED'}
          >
            Download &nbsp;<i className="bi bi-arrow-down-circle"></i>
          </button>
          
          <div className="position-relative d-inline-block">
            <button 
              className="ev-btn-blue-outline-round ev-bold-reg ms-1"
              onClick={() => gridThreadIdFromSLR ? handleDirectMigrate() : setShowThreadPanel(!showThreadPanel)}
              disabled={isLoading || data.length === 0}
            >
              <i className="bi bi-arrow-right-circle"></i> Migrate to Data Collection
            </button>
            
            {!gridThreadIdFromSLR && (
            <TextractorThreadPanel
              isOpen={showThreadPanel}
              onClose={() => setShowThreadPanel(false)}
              onSuccess={(message) => {
                setPopupSuccessMessage(message);
                setIsSuccessPopupVisible(true);
              }}
              onError={(message) => {
                setPopupErrorMessage(message);
                setIsErrorPopupVisible(true);
              }}
              onProcessingStart={(message) => {
                setProcessingMessage(message);
                setIsProcessingPopupVisible(true);
              }}
              onProcessingEnd={() => {
                setIsProcessingPopupVisible(false);
              }}
              modelId={modelId}
              modelName={tableName || modelName}
            />
            )}
          </div>
          
          <button 
            className="ev-btn-lightblue-fill-round ev-bold-reg ms-1" 
            onClick={handleRefresh}
            disabled={isLoading}
          >
            <i className="bi bi-arrow-repeat"></i> Refresh 
          </button>
        </div>
      </div>

      {isLoading && (
        <div className="text-center align-items-center justify-content-center d-flex vh-55">
          <div className="spinner-border text-primary" role="status">
            <span className="visually-hidden">Loading...</span>
          </div>
        </div>
      )}
      
      {error && (
        <div className="alert alert-danger mb-3">
          {error}
        </div>
      )}
      
      {!isLoading && !error && (
        <div className="scrollable-element vh-55">
          <table className="table table-custom1">
            <thead>
              <tr>
                {isEditMode && (
                  <th className="w-3">
                    <input
                      type="checkbox"
                      className='form-check-input'
                      checked={isAllSelected}
                      ref={checkbox => {
                        if (checkbox) checkbox.indeterminate = isSomeSelected;
                      }}
                      onChange={(e) => handleSelectAll(e.target.checked)}
                    /><br />
                    <small className="ev-xsmall-p-dark">&nbsp;</small>
                  </th>
                )}
                
                <th className="col-3">
                  Document Name
                  <br />
                  <small className="ev-xsmall-p-dark" >
                    &nbsp;
                  </small>
                </th>

                {isSLRFabric && (
                  <th>
                    Study ID
                    <br />
                    <small className="ev-xsmall-p-dark">&nbsp;</small>
                  </th>
                )}

                {isSLRFabric && (
                  <th>
                    Group ID
                    <br />
                    <small className="ev-xsmall-p-dark">&nbsp;</small>
                  </th>
                )}
                
                {columns.map((column, index) => (
                  <th key={index}>
                    {column.display_name || column.name}
                    <br />
                    <small className="ev-message-card2 mt-1 ev-btn-xsmall" >
                      {column.datatype}
                    </small>
                  </th>
                ))}
                
                {isEditMode && (
                  <th>
                    Actions
                  </th>
                )}
              </tr>
            </thead>
            <tbody>
              {mergedData.length === 0 ? (
                <tr>
                  <td colSpan={getTotalColSpan()} className="text-center">
                    No data found
                  </td>
                </tr>
              ) : (
                mergedData.map((row, rowIndex) => (
                  <tr key={rowIndex}>
                    {isEditMode && (
                      <td>
                        {!row.__synthetic && (
                          <input
                            type="checkbox"
                            className='form-check-input'
                            checked={isSelectedDel.includes(row.id)}
                            onChange={(e) => handleIndividualDelSelect(row.id, e.target.checked)}
                          />
                        )}
                      </td>
                    )}
                    
                    <td>
                      <span className="avatar-tooltip" data-tooltip={row.document_name || 'None'}>
                        <TrimContent text={row.document_name} maxLength={15} /> &nbsp;
                        <button 
                          onClick={() => handlePreviewDocument(row.doc_id, row.document_name)} 
                          className="ms-2 ev-btn-lightblue-fill-round ev-btn-xsmall"
                        >
                          View <i className="bi bi-eye"></i> 
                        </button>
                      </span>
                      {statusByDocId[row.doc_id] && (
                        <div className="mt-1">
                          <span
                            onMouseEnter={(e) => handleStatusBadgeEnter(e, statusByDocId[row.doc_id])}
                            onMouseLeave={handleStatusBadgeLeave}
                          >
                            <StatusBadgeXSm status={statusByDocId[row.doc_id].status} />
                          </span>
                        </div>
                      )}
                    </td>

                    {isSLRFabric && (
                      <td>
                        {row.__synthetic ? '-' : <TrimContent text={row.study_id || '-'} maxLength={15} />}
                      </td>
                    )}

                    {isSLRFabric && (
                      <td>
                        {row.__synthetic ? '-' : <TrimContent text={row.grouped_id || '-'} maxLength={15} />}
                      </td>
                    )}
                    
                    {columns.map((column, colIndex) => (
                      <td key={colIndex}>
                        {row.__synthetic ? <span>-</span> : renderCell(row, rowIndex, column)}
                      </td>
                    ))}
                    
                    {isEditMode && (
                      <td>
                        {!row.__synthetic && (
                          <button
                            className='ev-btn-red-fill-round ev-radius-xsmall pe-2 ps-2  ms-2 avatar-tooltip'
                            onClick={() => handleDeleteClickedByButton(row.id)}
                            disabled={isLoading}
                          >
                            <i className='bi bi-trash'></i>
                          </button>
                        )}
                      </td>
                    )}
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      )}
      
      {/* Pagination */}
      <div className="d-flex justify-content-between align-items-center mt-3">
        <div className="ev-small-p-dark">
          Showing {mergedData.length} of {total + syntheticFailedRows.length} entries
        </div>
        <div className="d-flex gap-2">
          <button 
            className="ev-btn-light-fill" 
            disabled={page === 1 || isLoading}
            onClick={() => setPage(page - 1)}
          >
            <i className="bi bi-chevron-left"></i> 
          </button>
          <button className="ev-btn-light-fill" disabled>
            Page {page} of {Math.max(1, Math.ceil(total / size))}
          </button>
          <button 
            className="ev-btn-light-fill" 
            disabled={page >= Math.ceil(total / size) || total === 0 || isLoading}
            onClick={() => setPage(page + 1)}
          >
            <i className="bi bi-chevron-right"></i>
          </button>
        </div>
        <div>
          <PaginationControls 
            initialSize={size}
            onSizeChange={(newSize) => {
              setSize(newSize);
              setPage(1);
            }}
          />
        </div>
      </div>

      {/* Document Preview Component */}
      <DocumentPreview 
        isOpen={previewOpen}
        onClose={closePreview}
        docId={previewDocId}
        documentName={previewDocumentName}
        highlightText={previewHighlightText}
        highlightChunkContent={previewChunkContent}
      />

      {/* Reusable Delete Confirmation Modal */}
      <DeleteConfirmationModal
        show={showDeleteConfirm}
        onClose={() => {
          setShowDeleteConfirm(false);
          setIsSelectedDel([]);
        }}
        onConfirm={handleConfirmDelete}
        objectName="data"
        itemName={`${isSelectedDel.length} row(s) from ${tableName}`}
        bodyContent={getDeleteModalBodyContent()}
      />

      {/* Run Confirmation Modal */}
      <ConfirmationModal
        show={showRunConfirm}
        onClose={() => setShowRunConfirm(false)}
        onConfirm={() => handleRunModel(false)}
        title="Confirm Run"
        message={`Are you sure you want to start the process: ${tableName}?`}
        confirmButtonText="Run Model"
        confirmButtonClass="btn-primary"
        bodyContent={getRunModalBodyContent()}
      />

      {/* Restart Confirmation Modal */}
      <ConfirmationModal
        show={showRestartConfirm}
        onClose={() => setShowRestartConfirm(false)}
        onConfirm={() => handleRunModel(true)}
        title="Confirm Restart Model"
        message={`Are you sure you want to restart the process: ${tableName}?`}
        confirmButtonText="Restart Model"
        confirmButtonClass="btn-danger"
        bodyContent={getRestartModalBodyContent()}
      />

      {/* Exit Edit Mode Confirmation Modal */}
      <ConfirmationModal
        show={pendingExitEditMode}
        onClose={() => setPendingExitEditMode(false)}
        onConfirm={confirmExitAndSave}
        title="Unsaved Changes"
        message="You have unsaved changes. Do you want to save before exiting edit mode?"
        confirmButtonText="Save & Exit"
        confirmButtonClass="btn-primary"
        cancelButtonText="Discard & Exit"
        onCancel={confirmExitWithoutSaving}
      />

      {/* Refresh Discard Confirmation Modal */}
      <ConfirmationModal
        show={pendingRefreshDiscard}
        onClose={() => setPendingRefreshDiscard(false)}
        onConfirm={confirmDiscardAndRefresh}
        title="Discard Unsaved Changes?"
        message="You have unsaved changes. Refreshing will discard these changes. Continue?"
        confirmButtonText="Discard & Refresh"
        confirmButtonClass="btn-danger"
      />

      {/* Run/Restart with unsaved changes */}
      <ConfirmationModal
        show={!!pendingRunAction}
        onClose={() => setPendingRunAction(null)}
        onConfirm={() => {
          const action = pendingRunAction;
          setPendingRunAction(null);
          if (action === 'run') setShowRunConfirm(true);
          else setShowRestartConfirm(true);
        }}
        title="Unsaved Changes"
        message={`You have unsaved changes. ${pendingRunAction === 'restart' ? 'Restarting will clear all existing data.' : 'Running will not save your edits.'} Continue without saving?`}
        confirmButtonText="Continue"
        confirmButtonClass="btn-danger"
      />

      {/* Popups */}
      {isProcessingPopupVisible && (
        <ProcessingPopup message={processingMessage} />
      )}
      
      {isErrorPopupVisible && (
        <ErrorPopup 
          onClose={closeErrorPopup}
          message={popupErrorMessage}
        />
      )}
      
      {isSuccessPopupVisible && (
        <SuccessPopup 
          onClose={closeSuccessPopup}
          message={popupSuccessMessage}
        />
      )}

      {/* Hover popup for doc status notes (shown for any status with notes — full/partial/failed) */}
      {statusHoverInfo && (
        <div
          className="ev-card-4"
          style={{
            position: 'fixed',
            bottom: statusHoverInfo.bottom,
            left: statusHoverInfo.left,
            width: 400,
            zIndex: 9999,
            pointerEvents: 'none'
          }}
        >
          <div className="ev-bold-reg mb-1 ev-reg-p-dark">
            {statusHoverInfo.notes}
          </div>
          <div></div>
        </div>
      )}
    </div>
  );
};

export default ModelDataViewer;